import * as Crypto from 'expo-crypto'
import { all, put, putResolve, call, race, select, take, fork } from 'redux-saga/effects'
import Toast from 'react-native-toast-message'

// Actions
import LoginActions, { selectAccessToken } from 'APP/Redux/LoginRedux'
import AccountLinkingActions from 'APP/Redux/AccountLinkingRedux'
import StartupActions from 'APP/Redux/StartupRedux'
import PatientActions from 'APP/Redux/PatientRedux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import { familyActions } from 'APP/Store/Family'
import { preloadActions } from 'APP/Store/Preload'
import DeeplinkActions from 'APP/Redux/DeeplinkRedux'

//Types
import { LoginTypes } from 'APP/Redux/LoginRedux'
import { AppSessionTypes } from 'APP/Redux/AppSessionRedux'

// Sagas
import {
  startupNavigation,
  navigateWithEmailVerified,
  requestMessagingCredentials,
  fetchUserInfo,
  navigateWithPermission,
  patientState,
  fetchPatientProfile,
  fetchProfileData,
  fetchScribeV2User,
} from './PatientSagas'
import { registerPushToken, registerPushVoipToken, unregisterPushToken } from './StartupSagas'
import { requestAccountLinking } from './AccountLinkingSagas'
import { deeplinkState, getPrimaryConnection } from './DeeplinkSaga'

// Libs
import {
  getExpiration,
  getCustomClaims,
  isAccessTokenValid,
  shouldResyncWebSessionForUserChange,
} from 'APP/Lib/AuthHelpers'

// Services
import authClient from 'APP/Services/Auth'
import sprigClient from 'APP/Services/Sprig'
import { identifyInsiderUser } from 'APP/Services/Insider'

import I18n from 'APP/I18n'
import { biometricsAvailable, APP_LOCK_STATE, triggerBiometrics } from 'APP/Lib/AppLock'
import Analytics, { LAUNCH_CATEGORY } from 'APP/Services/Analytics'
import { navigationRef as Nav } from 'APP/Nav'
import * as NestedNavHelper from 'APP/Nav/NestedNavHelper'
import {
  getMaxPublicSessionTimestamp,
  MINUTE_IN_MILLISECOND,
  stashDeferredDeeplink,
} from 'APP/Lib/Utilities'
import { PublicComputerType } from 'APP/Lib/Enums'
import { delay } from 'typed-redux-saga'
import {
  getSecureStorageEntry,
  removeSecureStorageEntry,
  setSecureStorageEntry,
} from 'APP/Lib/SecureStorage'
import { getSecureStorageKeys, securelyPersistedLoginEntries } from 'APP/Config/ReduxPersist'
import { logger, trackDdEvent } from 'APP/Lib/Datadog'
import { REGISTRATION_ACTIONS } from 'APP/Lib/Datadog/constants'
import { isEmbeddedEnvironment, getNativeModule } from 'APP/BrownfieldBridge/utils'
import { popToNative } from 'APP/BrownfieldBridge/BrownfieldBridgeInternal'
import { getPersistor } from 'APP/Store/RehydrateStore'
import {
  clearAll as clearSeamlessState,
  peekDestinationOrigin,
  stashDestination,
} from 'APP/Lib/SeamlessDestination'
import {
  SESSION_STORAGE_KEYS,
  getSessionItem,
  removeSessionItem,
  setSessionItem,
} from 'APP/Lib/SessionStorage'
import { isMobile, isWeb } from 'APP/Helpers/checkPlatform'
import Silkroad from 'APP/Services/Silkroad'

export const MISSING_REFRESH_TOKEN_ERROR_CODE = 'MISSING_REFRESH_TOKEN'

export const fullState = (state) => state
export const loginState = (state) => state.login
export const appState = (state) => state.app

// Auth0 error codes that mean the member mistyped or is rate-limited — expected user errors,
// not regressions. Kept out of Datadog so a real login-flow break isn't buried under them.
const EXPECTED_CREDENTIAL_ERRORS = [
  'invalid_grant',
  'invalid_user_password',
  'access_denied',
  'too_many_attempts',
  'mfa_required',
  'unauthorized',
]
const isExpectedCredentialError = (e) => {
  const code = e?.error || e?.code || e
  return typeof code === 'string' && EXPECTED_CREDENTIAL_ERRORS.includes(code)
}

export const minimumProfileState = (state) => {
  return {
    id: state.patient.profile.id,
    user_id: state.patient.profile.user_id,
    givenName: state.patient.profile.givenName,
    familyName: state.patient.profile.familyName,
    email: state.patient.profile.email,
  }
}

export const isMinimumProfileComplete = (profile) => {
  return profile.id && profile.user_id && profile.givenName && profile.familyName && profile.email
}

export function* retrieveLoginData() {
  const { hydrated } = yield select(loginState)
  if (hydrated) {
    return
  }
  try {
    const keys = getSecureStorageKeys()
    let results = yield all(keys.map((key) => call(getSecureStorageEntry, key)))

    const entries = securelyPersistedLoginEntries.reduce((acc, key, index) => {
      acc[key] = results[index]
      return acc
    }, {})

    yield put(LoginActions.rehydrateReducer(entries))
  } catch (e) {
    logger.error('Failed to retrieve login data from secure storage', {}, e)
  }
}

export function* persistLoginData(data) {
  try {
    const loginDataSecureStorageKeys = getSecureStorageKeys()
    yield all(
      securelyPersistedLoginEntries
        .map((key) => data[key])
        .map((value, index) =>
          call(setSecureStorageEntry, loginDataSecureStorageKeys[index], value)
        )
    )
  } catch (e) {
    logger.error('Failed to persist login data to secure storage', {}, e)
  }
}

export function* cleanupAuthData() {
  try {
    const keys = getSecureStorageKeys()
    yield all(keys.map((key) => call(removeSecureStorageEntry, key)))
  } catch (e) {
    logger.error('Failed to clean up auth data from secure storage', {}, e)
  }
}

/**
 * Handles user authentication through various login methods (Auth0, exchange token, signup)
 * and manages the complete login flow including success/failure states and brownfield integration.
 *
 * @param {Object} params - Additional login parameters
 * @param {Object} action - Redux action containing login request data
 * @param {string} action.type - Login action type (LOGIN_REQUEST, LOGIN_REQUEST_WITH_EXCHANGE_TOKEN, etc.)
 * @param {string} [action.exchangeToken] - Token for exchange-based authentication
 * @param {string} [action.connection] - Auth0 connection to use
 * @param {Object} [action.options] - Additional login options
 *
 * @throws {Error} Possible error scenarios:
 *
 * **Authentication Errors:**
 * - `Unknown WebAuth error` - Generic Auth0 WebAuth failure (logged to Datadog)
 * - `Invalid credentials` - Wrong username/password or invalid tokens
 * - `Account locked` - User account has been locked due to security policies
 * - `Network timeout` - Request timeout during authentication
 * - `Connection refused` - Auth0 service unavailable
 *
 * **Token/Session Errors:**
 * - `Invalid exchange token` - Provided exchange token is malformed or expired
 * - `Token parsing failed` - Unable to decode JWT tokens or custom claims
 * - `Session expired` - Current session has expired and cannot be renewed
 * - `Refresh token invalid` - Stored refresh token is no longer valid
 *
 * **User Provisioning Errors:**
 * - `Failed to run login actions` - MultiPass user provisioning failure (navigates to loginErrorScreen)
 * - `User not found` - User account doesn't exist in the system
 * - `Account not verified` - User email not verified yet
 * - `Tenant mismatch` - User belongs to different tenant than expected
 *
 * **Network/Service Errors:**
 * - `NETWORK_ERROR` - General network connectivity issues
 * - `TIMEOUT_ERROR` - Request timed out
 * - `Service unavailable` - Backend services are down
 * - `Rate limit exceeded` - Too many login attempts
 *
 * **Configuration Errors:**
 * - `Invalid client configuration` - Auth0 client misconfigured
 * - `Missing required parameters` - Required fields not provided
 * - `Unsupported connection type` - Unknown Auth0 connection
 *
 * **Account Linking Errors:**
 * - `Account linking failed` - Error during secondary account linking process
 * - `SSO link status invalid` - Problem with SSO account linking state
 *
 * **Brownfield Integration Errors:**
 * - `Bridge communication failed` - Unable to communicate with native bridge
 * - `Event trigger failed` - Error sending events to brownfield container
 *
 * **Data Processing Errors:**
 * - `Profile fetch failed` - Error retrieving user profile after login
 * - `Analytics attachment failed` - Error attaching tenant data to analytics
 * - `Push token registration failed` - Error registering device tokens
 *
 * **Side Effects on Error:**
 * - Sets `isLoggingIn` to false
 * - Dispatches `loginFailure` action with error details
 * - May trigger logout if `triggerLogoutOnFailure` is true
 * - Sends failure event to brownfield bridge in embedded mode
 * - May navigate to `loginErrorScreen` for specific errors
 * - Logs critical errors to Datadog for monitoring
 */
/**
 * @param {object} [params]
 * @param {object} [action]
 */
export function* login(params, action) {
  yield put(LoginActions.setIsLoggingIn(true))
  const { accessToken, refreshToken } = yield select(loginState)
  const {
    triggerStartupNav,
    triggerLogoutOnFailure,
    triggerSilentlogin = false,
    isEphemeralSession = true,
    openSignUp = false,
    saveSecondaryAccessToken = false,
    withHash = false,
    skipLoginPrompt = false,
  } = { ...params, ...action?.options }

  try {
    let credentials
    if (action?.type === LoginTypes.LOGIN_REQUEST_WITH_EXCHANGE_TOKEN) {
      credentials = yield call(authClient.loginWithExchangeToken, {
        subjectToken: action?.exchangeToken,
      })
    } else {
      const auth0Method = withHash
        ? authClient.parseHash
        : openSignUp
        ? authClient.signup
        : authClient.authorize

      // On web, authorize/signup are full-page redirects that wipe the deeplink store
      // Stash pending deeplink so it can be followed on return.
      if (!withHash) {
        const deeplink = yield select(deeplinkState)
        stashDeferredDeeplink(deeplink)

        // Existing-account linking needs the SSO token after Auth0 redirects away.
        // Native keeps the saga alive; web must stash intent in sessionStorage.
        if (isWeb() && saveSecondaryAccessToken) {
          setSessionItem(SESSION_STORAGE_KEYS.HAS_PENDING_ACCOUNT_LINK, 'true')
        } else {
          removeSessionItem(SESSION_STORAGE_KEYS.HAS_PENDING_ACCOUNT_LINK)
        }
      }

      const loginHint =
        action?.type === LoginTypes.ACCOUNT_LINK_LOGIN_REQUEST ? action?.loginHint : undefined

      credentials = yield call(auth0Method, {
        connection: action?.connection,
        triggerSilentlogin,
        isEphemeralSession,
        loginHint,
        skipLoginPrompt,
      })
    }

    if (credentials) {
      const pendingAccountLink = Boolean(
        getSessionItem(SESSION_STORAGE_KEYS.HAS_PENDING_ACCOUNT_LINK)
      )
      if (pendingAccountLink) {
        removeSessionItem(SESSION_STORAGE_KEYS.HAS_PENDING_ACCOUNT_LINK)
      }

      // Prefer explicit saga flag (native / same-page). On web hash return, restore from
      // sessionStorage + the SSO accessToken still in state from before the redirect.
      const shouldSaveSecondary = saveSecondaryAccessToken || pendingAccountLink
      const secondaryAccessToken = shouldSaveSecondary ? accessToken : undefined
      const customClaims = getCustomClaims(credentials.accessToken)
      const { sso_link_status: ssoLinkStatus } = customClaims

      yield putResolve(
        LoginActions.loginSuccess(
          undefined, // Username is not available for universal login
          credentials.idToken,
          credentials.accessToken,
          credentials.refreshToken ? credentials.refreshToken : refreshToken,
          getExpiration(credentials.expiresIn),
          customClaims,
          secondaryAccessToken,
          action?.exchangeToken
        )
      )

      Analytics.attachEntity('tenant', { tenant_id: customClaims.tenant_id })
      sprigClient.setVisitorAttribute('tenant_id', `${customClaims.tenant_id}`)

      // Funnel end (native only) — pairs with seamless_sso_started. Native auth resolves
      // credentials in-place here, so this is where a native seamless completion lands. Web
      // completes via the token-hash return in StartupSagas instead, so this is gated to mobile
      // to avoid double-counting. Peek (not consume) so navigation still gets the stash.
      if (isMobile() && peekDestinationOrigin() === 'seamless') {
        logger.info('seamless_sso_success', { connection: getPrimaryConnection() })
      }

      if (secondaryAccessToken) {
        yield call(requestAccountLinking)
        return
      }

      if (yield call(resyncWebSessionAfterUserChange, accessToken, credentials.accessToken)) {
        return
      }

      // Authentication succeeded, now entering onboarding/setup phase
      yield put(LoginActions.setIsOnboardingInProgress(true))
      yield call(processLoginSuccess, ssoLinkStatus, triggerStartupNav)
    } else {
      // Silent login found no active session — reset so the user isn't stuck.
      if (isEmbeddedEnvironment()) {
        popToNative(true)
      }
    }
  } catch (e) {
    // Clear web account-link intent if Auth0 fails/cancels before hash return can consume it.
    removeSessionItem(SESSION_STORAGE_KEYS.HAS_PENDING_ACCOUNT_LINK)

    // react-native-auth0 uses different cancellation codes per platform:
    //   iOS:     USER_CANCELLED
    //   Android: a0.session.user_cancelled
    //   Android: a0.session.browser_terminated (Chrome Custom Tab closed by system)
    const isUserCancellation =
      e.code === 'USER_CANCELLED' ||
      e.code === 'a0.session.user_cancelled' ||
      e.code === 'a0.session.browser_terminated'

    if (isUserCancellation) {
      yield put(
        LoginActions.loginFailure({
          message: e.message,
          cause: e.cause,
          ...e,
        })
      )
      if (isEmbeddedEnvironment()) {
        popToNative(true)
      }
      return
    }

    // Silent SSO attempt with no active session — expected, not an error worth logging.
    // Leave the user on WelcomeScreen to log in manually.
    const isSilentAuthNoSession =
      triggerSilentlogin && (e.error === 'login_required' || e.error === 'interaction_required')

    if (isSilentAuthNoSession) {
      // Expected outcome (member had no IdP session), not an error. Log to Datadog (info, not
      // error) so it can be told apart from real seamless SSO failures in monitoring.
      logger.info('seamless_sso_no_session', { error: e.error })
      yield put(LoginActions.loginFailure({ message: e.error, cause: e.error_description, ...e }))
      return
    }

    // Update login redux state
    yield put(
      LoginActions.loginFailure({
        message: e.message,
        cause: e.cause,
        ...e,
      })
    )

    // Log to Datadog. Include the connection so seamless SSO (e.g. sunlife) failures are
    // filterable — the Auth0 error and Datadog global attributes don't carry it otherwise.
    logger.error('Login failed', { connection: action?.connection }, e)

    // MultiPass user provisioning error
    if (e?.message === 'Failed to run login actions.') {
      yield call(Nav.navigate, 'loginErrorScreen')
      return
    }

    if (triggerLogoutOnFailure) {
      yield call(logout, { reason: 'Failed to login' })
    }

    if (isEmbeddedEnvironment()) {
      throw e
    }
  } finally {
    // Guarantee isLoggingIn always resets regardless of how the try block exits
    // (early return from account-linking, falsy credentials, cancellation, or throw).
    yield put(LoginActions.setIsLoggingIn(false))
    // Reset onboarding state now that all login and onboarding steps are complete
    yield put(LoginActions.setIsOnboardingInProgress(false))
  }
}

function* finalizeSession(ssoLinkStatus, emailVerified, triggerStartupNav = true) {
  if ((!ssoLinkStatus || ssoLinkStatus === 'linked') && emailVerified) {
    yield call(requestMessagingCredentials)
    yield call(registerTokens)
  }
  if (triggerStartupNav) {
    yield call(startupNavigation)
  }
  yield put(LoginActions.startRefreshAuthPoll())
  yield put(LoginActions.loginFetchComplete())
}

export function* processLoginSuccess(ssoLinkStatus, triggerStartupNav) {
  try {
    const { userInfo } = yield select(patientState)
    const emailVerified = userInfo?.email_verified ?? false
    yield call(finalizeSession, ssoLinkStatus, emailVerified, triggerStartupNav)
  } catch (e) {
    logger.error('Failed to process successful login', {}, e)

    throw e
  }
}

// Authentication via password-realm for post-account creation, to be removed once accounts are created in UL
export function* loginWithUsernameAndPassword(action, silent) {
  yield put(LoginActions.setIsLoggingIn(true))
  const { refreshToken } = yield select(loginState)
  const { username, password } = action

  try {
    const response = yield call(authClient.loginWithUsernameAndPassword, { username, password })
    const customClaims = getCustomClaims(response.accessToken)
    if (response.ok) {
      // dispatch successful logins
      yield putResolve(
        LoginActions.loginSuccess(
          username,
          response.idToken,
          response.accessToken,
          response.refreshToken ? response.refreshToken : refreshToken,
          getExpiration(response.expiresIn),
          customClaims,
          undefined // Secondary access token is only available through certain deeplinks for now
        )
      )

      Analytics.attachEntity('tenant', { tenant_id: customClaims.tenant_id })
      sprigClient.setVisitorAttribute('tenant_id', `${customClaims.tenant_id}`)

      if (!silent) {
        yield put(StartupActions.setPersistedLoginId(username))
        yield call(requestMessagingCredentials)
        yield call(startupNavigation)
        yield put(LoginActions.loginFetchComplete())
        yield call(registerTokens)
        yield put(LoginActions.startRefreshAuthPoll())
      }
    } else {
      yield put(LoginActions.stopRefreshAuthPoll())
      if (response?.error) {
        // Log only unexpected failures; wrong-credential/user errors are not regressions
        // and would drown the real signal in Datadog.
        if (!isExpectedCredentialError(response.error)) {
          logger.error('Password-realm login failed', {
            error: response.error?.error || response.error,
          })
        }
        yield put(LoginActions.loginFailure(response.error))
      } else {
        logger.error('Password-realm login failed', {
          problem: response.problem,
        })
        yield put(LoginActions.loginFailure(response.problem))
      }
    }
  } catch (e) {
    yield put(LoginActions.stopRefreshAuthPoll())
    if (!isExpectedCredentialError(e)) {
      logger.error('Password-realm login threw an unexpected error', {}, e)
    }
    yield put(
      LoginActions.loginFailure({
        message: e.message,
        cause: e.cause,
        ...e,
      })
    )
  } finally {
    yield put(LoginActions.setIsLoggingIn(false))
  }
}

export function* clearUserScopedState() {
  yield all([
    put(PatientActions.patientProfileClear()),
    put(PatientHistoryActions.patientHistoryClear()),
    put(familyActions.familyClear()),
    put(AccountLinkingActions.resetAccountLinking()),
    call([Crypto, Crypto.randomUUID]),
    sprigClient.logout(),
    put(LoginActions.stopRefreshAuthPoll()),
  ])
}

export function* resyncWebSessionAfterUserChange(previousAccessToken, newAccessToken) {
  if (isEmbeddedEnvironment()) return false

  const { readyToProcess } = yield select(deeplinkState)
  if (!shouldResyncWebSessionForUserChange(readyToProcess, previousAccessToken, newAccessToken)) {
    return false
  }

  yield call(clearUserScopedState)

  const { accessToken } = yield select(loginState)
  yield call(fetchUserInfo, accessToken)
  yield call(fetchPatientProfile)

  const { customClaims } = yield select(loginState)
  yield call(finalizeSession, customClaims?.sso_link_status, customClaims?.email_verified ?? false)
  return true
}

export function* logout(action) {
  yield put(LoginActions.setIsLoggingIn(false))

  const shouldUnregisterPushToken = action?.unregisterPushToken ?? false

  Analytics.trackStructuredEvent(
    'Logout',
    action?.reason ?? 'Unknown logout reason',
    shouldUnregisterPushToken ? 'manual' : 'automatic'
  )

  if (shouldUnregisterPushToken) {
    yield call(unregisterPushToken)
  }
  yield put(StartupActions.setAppLockToggle(false))

  const nativeModule = getNativeModule()
  const requestId = action?.embeddedLogoutRequestId ?? 'app-initiated'
  const reason = action?.reason ?? 'Unknown'

  try {
    yield call(clearUserScopedState)
    yield put(DeeplinkActions.setReadyToProcess(false))
    // Seamless SSO runs on web and native standalone, not embedded (OneApp authenticates
    // through its host app). For those, clear the pending seamless stashes and the persisted
    // connection so a logout can't leak them into the next session (stale deeplink replay,
    // auto re-login after logout). The seam clears sessionStorage on web, in-memory on native.
    if (!isEmbeddedEnvironment()) {
      clearSeamlessState()
      // Re-stash the reauth destination so fullStartup can pick it up after the Auth0 redirect.
      // Must happen here — authClient.logout triggers a full-page redirect on web, so anything
      // after that call never executes.
      if (action?.reauthPath) {
        stashDestination(action.reauthPath, action.reauthProps, 'seamless')
      }
      removeSessionItem(SESSION_STORAGE_KEYS.HAS_PENDING_ACCOUNT_LINK)
      yield put(LoginActions.clearConnection())
      const persistor = getPersistor()
      if (persistor) {
        yield call([persistor, persistor.flush])
      }
    }
    yield call(authClient.logout)
    Analytics.detachEntity('tenant')

    if (isEmbeddedEnvironment()) {
      yield put(preloadActions.resetPreload())
      yield put(LoginActions.clearState())
      // redux-persist debounces writes, so flush the cleared login state to
      // storage before popToNative unmounts the embedded app.
      const persistor = getPersistor()
      if (persistor) {
        yield call([persistor, persistor.flush])
      }

      nativeModule?.logoutCallbackFor({
        type: 'LOGOUT_SUCCESS',
        payload: { requestId, reason },
      })
      popToNative(true)
      return
    }

    yield call(Nav.reset, { index: 0, routes: [{ name: 'welcome' }] })
  } catch (error) {
    logger.error('Error while logging out', {}, error)

    if (isEmbeddedEnvironment()) {
      nativeModule?.logoutCallbackFor({
        type: 'LOGOUT_FAILURE',
        payload: {
          requestId,
          reason,
          error: error?.message ?? String(error),
        },
      })
      popToNative(true)
    }
  } finally {
    yield put(LoginActions.clearState())
    yield put(LoginActions.setIsLoggingOut(false))
  }
}

export function* registerTokens() {
  const { pushToken, pushVoipToken } = yield select(appState)
  if (pushToken) {
    yield fork(registerPushToken, { pushToken })
  }
  if (pushVoipToken) {
    yield fork(registerPushVoipToken, { pushVoipToken })
  }

  // Insider's equivalent of registration: the SDK handles its own token, but marketing
  // can't target the member until the identity is bound. Guarded because some callers
  // run before the profile is fetched or the email is verified.
  const patient = yield select(patientState)
  if (patient?.userInfo?.email_verified && patient?.profile?.id) {
    const insiderEmail = patient.userInfo.email || patient.profile.email
    yield call(identifyInsiderUser, insiderEmail, patient.profile.id)
  }
}

export function* checkBiometricsAvailable() {
  return yield call(biometricsAvailable)
}

export function* checkAppLockSupport() {
  const bioAvailable = yield call(biometricsAvailable)
  const app = yield select(appState)
  const appLockToggled = app && app.appLock && app.appLock.isToggled

  if (!appLockToggled) {
    return APP_LOCK_STATE.DISABLED
  } else if (!bioAvailable) {
    return APP_LOCK_STATE.ENABLED_BLOCKED
  } else {
    return APP_LOCK_STATE.ENABLED
  }
}

export function* triggerAppLock() {
  const result = yield call(triggerBiometrics)
  try {
    if (!result || !result.success) {
      yield put(LoginActions.logout('Failed app lock challenge'))
    } else {
      //proceed with refresh token if not complete
      yield call(NestedNavHelper.pop)
      // If we returned to the splash screen we need to refresh the token and run startup navigation
      if (Nav.getCurrentRoute()?.name === 'loading') {
        yield call(checkRenewAuth)
        yield call(startupNavigation)
      }
    }
  } catch {
    // If the checkRenewAuth fails and logs the user out, we need to catch and do nothing
    // to avoid breaking the root saga
  }
}

export function* toggleMfa() {
  const patient = yield select(patientState)
  const { accessToken, refreshToken } = yield select(loginState)
  const isMfaEnabled = patient?.userInfo?.user_metadata?.mfa_enabled ?? false

  try {
    const silkroad = Silkroad.create(accessToken)

    if (!isMfaEnabled) {
      yield call(silkroad.setMFA, { enable: true })
      // After enabling MFA, the user needs to enroll their second factor. We call authorize
      // without prompt: 'login' so Auth0 recognises the existing session and only shows
      // the MFA enrollment step — no credential re-entry required.
      yield call(login, {
        triggerStartupNav: false,
        triggerLogoutOnFailure: true, // If the auth fails for any reason we need to log the user out, which will force them to setup their second factor
        skipLoginPrompt: true,
      })
    } else {
      yield call(silkroad.setMFA, { enable: false })
      // Must refresh token to get the new user_metadata
      yield call(refreshAuth, refreshToken)
    }

    yield call(fetchUserInfo, accessToken, true)
    yield put(LoginActions.toggleMfaSuccess())
    if (!isMfaEnabled) {
      yield call(trackDdEvent, REGISTRATION_ACTIONS.mfaEnabled)
    }
  } catch (error) {
    logger.error('Error toggling MFA', {}, error)
    yield put(LoginActions.toggleMfaFailure())
  }
}

export function* activateAppLockToggle() {
  const result = yield call(triggerBiometrics)
  yield put(StartupActions.setAppLockToggle(Boolean(result && result.success)))
}

export function* refreshAuth(refreshToken) {
  const { accessToken: previousAccessToken } = yield select(loginState)

  try {
    const response = yield call(authClient.renewAuth, { refreshToken })

    if (!response?.accessToken) return false

    const customClaims = getCustomClaims(response.accessToken)

    yield put(
      LoginActions.refreshAuthSuccess(
        response.idToken,
        response.accessToken,
        response.refreshToken ? response.refreshToken : refreshToken,
        getExpiration(response.expiresIn),
        customClaims
      )
    )
    Analytics.attachEntity('tenant', { tenant_id: customClaims.tenant_id })
    sprigClient.setVisitorAttribute('tenant_id', `${customClaims.tenant_id}`)

    return yield call(resyncWebSessionAfterUserChange, previousAccessToken, response.accessToken)
  } catch (e) {
    yield put(
      LoginActions.refreshAuthFailure({
        message: e.message,
        cause: e.cause,
        ...e,
      })
    )
    Analytics.trackLoadingTime({
      action: LAUNCH_CATEGORY.EXIT,
      label: 'Refresh token failed',
    })

    throw e
  }
}

export function* emailVerificationCheck(silentCheck) {
  const { accessToken, refreshToken } = yield select(loginState)

  let emailVerified = false

  yield put(LoginActions.emailVerificationCheck(silentCheck))
  try {
    const userInfoResult = yield call(fetchUserInfo, accessToken, true)

    if (!userInfoResult.ok) {
      yield put(LoginActions.emailVerificationCheckFailure())
      return false
    }

    emailVerified = userInfoResult.userInfo?.email_verified === true

    if (emailVerified) {
      // we need to fetch a new token when the user is verified to have the tenant
      yield call(refreshAuth, refreshToken)
      yield call(fetchPatientProfile)
      yield all([call(fetchScribeV2User), call(requestMessagingCredentials), call(registerTokens)])
      yield call(fetchProfileData)

      yield put(LoginActions.emailVerificationCheckSuccess())
      yield call(trackDdEvent, REGISTRATION_ACTIONS.emailVerified)
      yield call(navigateWithEmailVerified)
    } else {
      yield put(LoginActions.emailVerificationCheckFailure())
    }
  } catch (err) {
    yield put(LoginActions.emailVerificationCheckFailure())
    throw err
  }

  return emailVerified
}

export function* watchEmailVerificationCheckRequests() {
  while (true) {
    const { silentCheck, logout } = yield race({
      userRequestedCheck: take(LoginTypes.REQUEST_EMAIL_VERIFICATION_CHECK),
      silentCheck: take(AppSessionTypes.SET_APP_FOREGROUNDED),
      logout: take(LoginTypes.LOGOUT),
    })

    // Prevent calling email verification check when there is a countdown block going on
    const { emailVerificationBlock } = yield select(loginState)

    if (emailVerificationBlock) {
      break
    }

    if (logout) {
      break
    }

    const emailVerified = yield call(emailVerificationCheck, !!silentCheck)

    if (emailVerified) {
      break
    }
  }
}

export function* requestEmailVerificationResend() {
  try {
    const accessToken = yield select(selectAccessToken)
    const silkroad = Silkroad.create(accessToken)

    const communication_language = I18n.communicationLanguage
    yield call(silkroad.resendVerificationEmail, { communication_language })
    yield put(LoginActions.emailVerificationResendSuccess())
  } catch (_error) {
    yield put(LoginActions.emailVerificationResendFailure())
  }
}

export function* renewAuth() {
  const { refreshToken, accessToken } = yield select(loginState)
  // Web renews via cookie-based checkSession and never has a refresh token.
  // Only enforce the missing-token guard on mobile where refresh tokens are required.
  if (isMobile() && !refreshToken) {
    if (isAccessTokenValid(accessToken)) {
      return
    }
    yield put(LoginActions.logout('Missing refresh token'))

    // Throw to abort the calling saga — without this, downstream steps would
    // continue executing against a cleared auth state.
    const error = new Error('Cannot renew auth: no refresh token available')
    error.code = MISSING_REFRESH_TOKEN_ERROR_CODE
    throw error
  }

  // attempt the token renewal 3 times if we get a timeout or network error
  let attempt = 0
  let originalError
  while (attempt < 3) {
    attempt++
    try {
      return yield call(refreshAuth, refreshToken)
    } catch (error) {
      originalError = error
      // If we get either a network error or timeout we retry after a short delay
      const problem = error?.cause?.problem
      if (
        problem === 'NETWORK_ERROR' ||
        problem === 'TIMEOUT_ERROR' ||
        problem === 'CONNECTION_ERROR'
      ) {
        logger.warn('auth:refresh_network_error', { problem, attempt })
        yield delay(150)
        break
      }

      // Other "non recoverable" error we:
      // 1. log the user out
      // 2. navigate to the welcome screen
      // 3. show a toast
      // 4. throw the error so it can cascade up the sagas
      logger.warn('auth:logout_refresh_failed', {
        error_code: error?.code,
        error_message: error?.message,
        http_status: error?.cause?.status,
        attempt,
      })
      yield put(LoginActions.logout('Failed to refresh token'))

      // If not in the embedded env, navigate the standard welcome screen with
      // error toast
      if (!isEmbeddedEnvironment()) {
        yield call(Nav.reset, { index: 0, routes: [{ name: 'welcome' }] })

        Toast.show({
          text1: I18n.t('WelcomeScreen.errorTitle'),
          text2: I18n.t('WelcomeScreen.errorSubtitle'),
        })
      }

      throw error
    }
  }
  // Throw original error
  throw originalError
}
export function* checkRenewAuth() {
  const { accessToken, tokenExpiration } = yield select(loginState)

  // On web, we refresh the session when we're 8 minutes away from the session expiry (which last 1 hour)
  // On mobile, we refresh the tokens when we're 8 minutes away from the token expiry
  const expiryOffset = 8 * MINUTE_IN_MILLISECOND
  const expiryTime = Date.now() + expiryOffset
  const shouldRenewAuth = expiryTime > tokenExpiration
  if (shouldRenewAuth && accessToken) {
    try {
      yield call(renewAuth)
    } catch (error) {
      // Network errors are transient — swallow them so the poll keeps running
      // and retries on the next interval. Non-network errors (e.g. invalid
      // refresh token) are re-thrown so they can propagate to the logout flow.
      const problem = error?.cause?.problem

      if (
        problem === 'NETWORK_ERROR' ||
        problem === 'TIMEOUT_ERROR' ||
        problem === 'CONNECTION_ERROR'
      ) {
        logger.warn('auth:poll_refresh_network_error', { problem })
        return
      }

      throw error
    }

    // If we had to renew the Auth, we have to restart the mattermost websocket
    yield call(requestMessagingCredentials)
  }
}

export function* addRememberedDevice(action) {
  // Only add the device if the user checked the "dont ask me again on this computer"
  if (!action.data.dontAskPublicComputer) {
    yield call(navigateWithPermission)
  } else {
    const { accessToken, deviceId } = yield select(loginState)
    const silkroad = Silkroad.create(accessToken)

    const publicValue = action.data.publicComputer === PublicComputerType.ANSWERED_YES

    yield call(silkroad.addDevice, { device_id: deviceId, is_public: publicValue })
    yield call(navigateWithPermission)
  }
}

export function* checkPublicComputer() {
  const { accessToken, publicComputer, lastActivity } = yield select(loginState)
  if (
    accessToken &&
    publicComputer === PublicComputerType.ANSWERED_YES &&
    lastActivity &&
    getMaxPublicSessionTimestamp() >= lastActivity
  ) {
    // The user is on a public computer and they logged out for the last time more than the
    //public session length on a public computer, so we log them out
    yield put(LoginActions.logout())
  }
}
